import { queryKeys } from "@bso/shared";
import { createRoute } from "@tanstack/solid-router";
import { Link, useNavigate } from "@/lib/u-router";
import { appRoute } from "./route";
import { createEffect, createMemo, createResource, createSignal, For, Show } from "solid-js";
import { cleanVerseText, fetchChapterVerses } from "@/utils/chapter-verses-cache";
import {
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  Edit3,
  Filter,
  Highlighter,
  Layers,
  Palette,
  Tag,
  Trash2,
  X,
} from "lucide-solid";
import { bibleBooks, bookGroups, getBookNameDescriptor, idToAbbr } from "@/utils/bible-books-data";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import {
  applyVerseGroupFilters,
  compareByCanonicalOrder,
  compareByDate,
  compareVerseRefs,
} from "@/utils/verse-collections";
import { formatVerseReference } from "@/utils/verse-reference";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { EmptyIllustration } from "@/components/ui/empty-illustration";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { registerUndo } from "@/lib/undo";
import { createPowerSyncQuery } from "@bso/shared/solid";
import {
  and,
  bible_highlight,
  bible_highlight_tag,
  bible_verse_highlight,
  eq,
  inArray,
  isNull,
  type PowerSyncDrizzleDB,
} from "@bso/powersync";

type HighlightsSearch = {
  order?: "DATE" | "CANONICAL" | "COLOR";
  b?: number;
  cat?: string;
  tag?: string;
  /**
   * Id del resaltado abierto en el drawer de edición. Hace el editor
   * **reabrible por URL** (`?edit=<id>`): pegar/recargar reabre el sheet sobre
   * ese resaltado. Dato del propio usuario: un tercero verá "no encontrado".
   */
  edit?: string;
};

interface GroupedHighlight {
  id: string;
  originId: string | null;
  rowIds: string[];
  color: string;
  createdAt: string;
  tags: string[];
  verses: {
    version: string;
    book: number;
    chapter: number;
    verse: number;
  }[];
}

const HIGHLIGHT_COLORS = [
  { name: msg`Yellow`, hex: "#eab308" },
  { name: msg`Green`, hex: "#22c55e" },
  { name: msg`Blue`, hex: "#3b82f6" },
  { name: msg`Pink`, hex: "#ec4899" },
];

export const highlightsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/highlights",
  validateSearch: (search: Record<string, unknown>): HighlightsSearch => {
    const { order } = search;
    const validOrder =
      order === "DATE" || order === "CANONICAL" || order === "COLOR" ? order : "DATE";
    return {
      order: validOrder,
      b: search.b ? Number(search.b) : undefined,
      cat: typeof search.cat === "string" && search.cat ? search.cat : undefined,
      tag: typeof search.tag === "string" && search.tag ? search.tag : undefined,
      edit: typeof search.edit === "string" && search.edit ? search.edit : undefined,
    };
  },
  component: HighlightsPage,
});

// Scripture text of the highlighted verses (legacy parity: highlight lists
// showed the verse text, not just the reference)
function HighlightVersesText(props: {
  verses: { version: string; book: number; chapter: number; verse: number }[];
  db: PowerSyncDrizzleDB | null;
}) {
  const { t } = useI18n();
  const MAX_SNIPPET_VERSES = 2;
  const snippetVerses = () => props.verses.slice(0, MAX_SNIPPET_VERSES);

  const [texts] = createResource(
    () => ({ verses: snippetVerses(), db: props.db }),
    async ({ verses, db }) => {
      const results = await Promise.all(
        verses.map(async (v) => {
          // Local-first (CC-11): reads bible_verse_content via PowerSync so the
          // highlight snippets render offline instead of crashing on the API.
          const chapterVerses = await fetchChapterVerses(db, v.version, v.book, v.chapter);
          const match = chapterVerses.find((cv) => cv.verse === v.verse);
          return match ? { verse: v.verse, text: cleanVerseText(match.text) } : null;
        }),
      );
      return results.filter((r) => r !== null);
    },
  );

  return (
    <Show when={(texts() ?? []).length > 0}>
      <p class="text-xs leading-relaxed text-foreground/75 font-serif line-clamp-3">
        <For each={texts()}>
          {(item) => (
            <span>
              <span class="align-super text-4xs font-bold text-muted-foreground mr-0.5">
                {item.verse}
              </span>
              {item.text}{" "}
            </span>
          )}
        </For>
        <Show when={props.verses.length > MAX_SNIPPET_VERSES}>
          <span class="text-muted-foreground font-sans text-3xs">
            (+{props.verses.length - MAX_SNIPPET_VERSES} {t(msg`more verses`)})
          </span>
        </Show>
      </p>
    </Show>
  );
}

function HighlightsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();

  const searchParams = highlightsRoute.useSearch();
  const orderParam = () => searchParams().order ?? "DATE";
  const bookFilter = () => searchParams().b;
  const categoryFilter = () => searchParams().cat;
  const tagFilter = () => searchParams().tag;
  const editId = () => searchParams().edit;

  const [showFilterDropdown, setShowFilterDropdown] = createSignal(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = createSignal(false);

  // Edit drawer state (legacy parity: update-highlight-screen). El drawer abierto
  // es **reabrible por URL** (`?edit=<id>`): el resaltado a editar se deriva de la
  // lista reactiva por id; los campos del formulario se siembran al abrir.
  const [editorColor, setEditorColor] = createSignal(HIGHLIGHT_COLORS[0]?.hex ?? "#eab308");
  const [editorTags, setEditorTags] = createSignal("");

  // PowerSync reactive query for flat highlight rows
  const highlights = createPowerSyncQuery({
    queryKey: () => queryKeys.highlights.mine(bookFilter(), session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }

      const conditions = [
        eq(bible_verse_highlight.user_id, u),
        isNull(bible_verse_highlight.deleted_at),
      ];

      const b = bookFilter();
      if (b !== undefined) {
        conditions.push(eq(bible_verse_highlight.book, b));
      }

      return dbInstance
        .select({
          highlightId: bible_verse_highlight.id,
          originId: bible_verse_highlight.origin_id,
          color: bible_verse_highlight.color,
          createdAt: bible_verse_highlight.created_at,
          version: bible_verse_highlight.version,
          book: bible_verse_highlight.book,
          chapter: bible_verse_highlight.chapter,
          verse: bible_verse_highlight.verse,
        })
        .from(bible_verse_highlight)
        .where(and(...conditions));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
  });

  // Tags of the user's highlight groups
  const tagsQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.highlights.tags(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({
          highlightId: bible_highlight_tag.highlight_id,
          tag: bible_highlight_tag.tag,
        })
        .from(bible_highlight_tag)
        .innerJoin(bible_highlight, eq(bible_highlight.id, bible_highlight_tag.highlight_id))
        .where(and(eq(bible_highlight.user_id, u), isNull(bible_highlight.deleted_at)));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
  });

  const tagsByOrigin = createMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of tagsQuery.data ?? []) {
      if (!row.highlightId || !row.tag) {
        continue;
      }
      const list = map.get(row.highlightId) ?? [];
      list.push(row.tag);
      map.set(row.highlightId, list);
    }
    return map;
  });

  const allTags = createMemo(() =>
    [...new Set((tagsQuery.data ?? []).flatMap((r) => (r.tag ? [r.tag] : [])))].toSorted((a, b) =>
      a.localeCompare(b),
    ),
  );

  // Group rows by their origin group (fallback: standalone row), then filter and sort
  const groupedHighlights = createMemo(() => {
    const rows = highlights.data ?? [];
    const groupsMap = new Map<string, GroupedHighlight>();

    for (const r of rows) {
      if (!r.highlightId) {
        continue;
      }

      const groupKey = r.originId ?? r.highlightId;
      let group = groupsMap.get(groupKey);
      if (!group) {
        group = {
          id: groupKey,
          originId: r.originId,
          rowIds: [],
          color: r.color ?? "#ff0000",
          createdAt: r.createdAt ?? new Date().toISOString(),
          tags: tagsByOrigin().get(groupKey) ?? [],
          verses: [],
        };
        groupsMap.set(groupKey, group);
      }

      group.rowIds.push(r.highlightId);
      if (r.book !== null && r.chapter !== null && r.verse !== null) {
        group.verses.push({
          version: r.version ?? "kjv",
          book: r.book,
          chapter: r.chapter,
          verse: r.verse,
        });
      }
    }

    let list = [...groupsMap.values()];

    // Sort verses inside each group canonically
    for (const g of list) {
      g.verses.sort(compareVerseRefs);
    }

    // Category + tag filters (legacy parity: bibleBookCategory; the book
    // filter already happened at the SQL level)
    list = applyVerseGroupFilters(list, { cat: categoryFilter(), tag: tagFilter() });

    // Sort the list of highlights based on order parameter
    const order = orderParam();
    if (order === "DATE") {
      list.sort(compareByDate);
    } else if (order === "CANONICAL") {
      list.sort(compareByCanonicalOrder);
    } else {
      list.sort((a, b) => a.color.localeCompare(b.color));
    }

    return list;
  });

  // El resaltado en edición se deriva de `?edit=` contra la lista reactiva. Si el
  // id no existe (enlace de otro usuario / borrado), el drawer simplemente no abre.
  const editingHighlight = createMemo<GroupedHighlight | null>(
    () => groupedHighlights().find((h) => h.id === editId()) ?? null,
  );

  // Siembra los campos del formulario al cambiar el resaltado en edición (apertura
  // por click o por URL). Sin entidad, no toca los campos.
  createEffect(() => {
    const h = editingHighlight();
    if (h) {
      setEditorColor(h.color);
      setEditorTags(h.tags.join(", "));
    }
  });

  /** Abre/cierra el drawer escribiendo `?edit=` (la URL es la fuente de verdad). */
  const setEditingHighlightUrl = (id: string | null) => {
    navigateWithSearch({ edit: id ?? undefined });
  };

  const handleDelete = async (highlight: GroupedHighlight) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }

    const now = new Date().toISOString();
    try {
      // Logical delete of every flat row of the group
      await dbInstance
        .update(bible_verse_highlight)
        .set({ deleted_at: now, updated_at: now })
        .where(inArray(bible_verse_highlight.id, highlight.rowIds));

      // Logical delete of the origin group (tags disappear with it)
      if (highlight.originId) {
        await dbInstance
          .update(bible_highlight)
          .set({ deleted_at: now, updated_at: now })
          .where(eq(bible_highlight.id, highlight.originId));
      }

      // Soft-delete → undo restores the group (L1); also recoverable from trash (L2).
      const rowIds = [...highlight.rowIds];
      const { originId } = highlight;
      registerUndo(
        {
          done: t(msg`Highlight moved to trash`),
          undoLabel: t(msg`Undo`),
          undone: t(msg`Undone`),
          failed: t(msg`Couldn't undo`),
        },
        async () => {
          const restoredAt = new Date().toISOString();
          await dbInstance
            .update(bible_verse_highlight)
            .set({ deleted_at: null, updated_at: restoredAt })
            .where(inArray(bible_verse_highlight.id, rowIds));
          if (originId) {
            await dbInstance
              .update(bible_highlight)
              .set({ deleted_at: null, updated_at: restoredAt })
              .where(eq(bible_highlight.id, originId));
          }
        },
      );

      if (editingHighlight()?.id === highlight.id) {
        setEditingHighlightUrl(null);
      }
    } catch (e) {
      console.error("Failed to logically delete highlight:", e);
    }
  };

  const handleEdit = (highlight: GroupedHighlight) => {
    // Solo abre por URL; el efecto de siembra rellena color/tags desde la entidad.
    setEditingHighlightUrl(highlight.id);
  };

  // Save color/tags changes (creates the origin group on the fly for rows
  // created before grouped highlights existed)
  const handleSaveEdit = async () => {
    const dbInstance = drizzle();
    const u = session.userId();
    const highlight = editingHighlight();
    if (!dbInstance || !u || !highlight) {
      return;
    }

    const now = new Date().toISOString();
    const color = editorColor();
    const tagsList = editorTags()
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    try {
      let { originId } = highlight;

      if (originId) {
        await dbInstance
          .update(bible_highlight)
          .set({ color, updated_at: now })
          .where(eq(bible_highlight.id, originId));
      } else {
        originId = crypto.randomUUID();
        // Group insert must precede tag inserts (server FK during sync)
        await dbInstance.insert(bible_highlight).values({
          id: originId,
          user_id: u,
          color,
          created_at: highlight.createdAt,
          updated_at: now,
        });
        await dbInstance
          .update(bible_verse_highlight)
          .set({ origin_id: originId, updated_at: now })
          .where(inArray(bible_verse_highlight.id, highlight.rowIds));
      }

      // Replace tags with the edited list
      await dbInstance
        .delete(bible_highlight_tag)
        .where(eq(bible_highlight_tag.highlight_id, originId));
      for (const tag of tagsList) {
        // oxlint-disable-next-line no-await-in-loop
        await dbInstance.insert(bible_highlight_tag).values({
          id: crypto.randomUUID(),
          highlight_id: originId,
          tag,
        });
      }

      // Keep flat rows (reader rendering) in sync with the group color
      await dbInstance
        .update(bible_verse_highlight)
        .set({ color, updated_at: now })
        .where(inArray(bible_verse_highlight.id, highlight.rowIds));

      setEditingHighlightUrl(null);
    } catch (e) {
      console.error("Failed to update highlight:", e);
    }
  };

  const navigateWithSearch = (patch: Partial<HighlightsSearch>) => {
    void navigate({
      to: "/highlights",
      search: {
        order: orderParam(),
        b: bookFilter(),
        cat: categoryFilter(),
        tag: tagFilter(),
        edit: editId(),
        ...patch,
      },
    });
  };

  const handleOrderChange = (order: "DATE" | "CANONICAL" | "COLOR") => {
    navigateWithSearch({ order });
  };

  const handleBookChange = (bookId: number | undefined) => {
    navigateWithSearch({ b: bookId });
    setShowFilterDropdown(false);
  };

  const handleCategoryChange = (cat: string | undefined) => {
    navigateWithSearch({ cat });
    setShowCategoryDropdown(false);
  };

  const handleTagChange = (tag: string | undefined) => {
    navigateWithSearch({ tag });
  };

  return (
    <div class="relative flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <Highlighter class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
              {t(msg`Highlighted Verses`)}
            </h1>
            <p class="text-xs text-muted-foreground mt-0.5">
              {t(msg`Your underlined and saved passages`)}
            </p>
          </div>
        </div>
      </div>

      {/* Control bar */}
      <div class="border-b border-border/20 px-4 md:px-8 py-3 md:py-4 bg-muted/20 flex flex-wrap gap-3 md:gap-4 items-center justify-between shrink-0">
        {/* Ordering */}
        <div class="flex items-center gap-1.5 bg-background border border-border/40 p-1 rounded-xl text-xs font-semibold shadow-2xs">
          <button
            type="button"
            onClick={() => {
              handleOrderChange("DATE");
            }}
            class="px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
            classList={{
              "bg-primary text-primary-foreground": orderParam() === "DATE",
              "hover:bg-muted text-muted-foreground": orderParam() !== "DATE",
            }}
          >
            <Calendar class="w-3.5 h-3.5" />
            <span>{t(msg`Date`)}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              handleOrderChange("CANONICAL");
            }}
            class="px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
            classList={{
              "bg-primary text-primary-foreground": orderParam() === "CANONICAL",
              "hover:bg-muted text-muted-foreground": orderParam() !== "CANONICAL",
            }}
          >
            <BookOpen class="w-3.5 h-3.5" />
            <span>{t(msg`Biblical Order`)}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              handleOrderChange("COLOR");
            }}
            class="px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
            classList={{
              "bg-primary text-primary-foreground": orderParam() === "COLOR",
              "hover:bg-muted text-muted-foreground": orderParam() !== "COLOR",
            }}
          >
            <Palette class="w-3.5 h-3.5" />
            <span>{t(msg`Color`)}</span>
          </button>
        </div>

        <div class="flex items-center gap-3">
          {/* Filtering by canonical category */}
          <div class="relative">
            <button
              type="button"
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown())}
              class="px-4 py-2 bg-background border border-border/40 hover:bg-muted/40 text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-2xs"
            >
              <Layers class="w-3.5 h-3.5 text-muted-foreground" />
              <span>{categoryFilter() ?? t(msg`All the Bible`)}</span>
              <ChevronDown class="w-3.5 h-3.5 text-muted-foreground" />
            </button>

            <ResponsiveSheet
              open={showCategoryDropdown()}
              onClose={() => setShowCategoryDropdown(false)}
              title={t(msg`Canonical category`)}
              dropdownClass="w-60"
            >
              {() => (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      handleCategoryChange(undefined);
                    }}
                    class="w-full text-left px-3 py-2.5 md:py-1.5 text-sm md:text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors"
                    classList={{
                      "bg-primary/10 text-primary font-semibold": !categoryFilter(),
                      "text-foreground/80": Boolean(categoryFilter()),
                    }}
                  >
                    {t(msg`All the Bible`)}
                  </button>
                  <For each={bookGroups}>
                    {(group) => (
                      <button
                        type="button"
                        onClick={() => {
                          handleCategoryChange(group.key);
                        }}
                        class="w-full text-left px-3 py-2.5 md:py-1.5 text-sm md:text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors"
                        classList={{
                          "bg-primary/10 text-primary font-semibold":
                            categoryFilter() === group.key,
                          "text-foreground/80": categoryFilter() !== group.key,
                        }}
                      >
                        {t(group.name)}
                      </button>
                    )}
                  </For>
                </>
              )}
            </ResponsiveSheet>
          </div>

          {/* Filtering by book */}
          <div class="relative">
            <button
              type="button"
              onClick={() => setShowFilterDropdown(!showFilterDropdown())}
              class="px-4 py-2 bg-background border border-border/40 hover:bg-muted/40 text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-2xs"
            >
              <Filter class="w-3.5 h-3.5 text-muted-foreground" />
              <span>
                {(() => {
                  const b = bookFilter();
                  return b === undefined ? t(msg`Filter by book`) : t(getBookNameDescriptor(b));
                })()}
              </span>
              <ChevronDown class="w-3.5 h-3.5 text-muted-foreground" />
            </button>

            <ResponsiveSheet
              open={showFilterDropdown()}
              onClose={() => setShowFilterDropdown(false)}
              title={t(msg`Filter by Book`)}
            >
              {() => (
                <>
                  <Show when={bookFilter()}>
                    <div class="flex items-center justify-end border-b border-border/20 pb-2 mb-2 px-2">
                      <button
                        type="button"
                        onClick={() => {
                          handleBookChange(undefined);
                        }}
                        class="text-3xs text-primary font-bold hover:underline flex items-center gap-0.5"
                      >
                        <X class="w-3 h-3" />
                        <span>{t(msg`View all`)}</span>
                      </button>
                    </div>
                  </Show>
                  <div class="space-y-0.5">
                    <For each={bibleBooks}>
                      {(book) => (
                        <button
                          type="button"
                          onClick={() => {
                            handleBookChange(book.id);
                          }}
                          class="w-full text-left px-3 py-2.5 md:py-1.5 text-sm md:text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors"
                          classList={{
                            "bg-primary/10 text-primary font-semibold": bookFilter() === book.id,
                            "text-foreground/80": bookFilter() !== book.id,
                          }}
                        >
                          {t(book.name)}
                        </button>
                      )}
                    </For>
                  </div>
                </>
              )}
            </ResponsiveSheet>
          </div>
        </div>
      </div>

      {/* Tag filter chips */}
      <Show when={allTags().length > 0}>
        <div class="border-b border-border/20 px-4 md:px-8 py-3 bg-muted/10 flex flex-wrap items-center gap-2 shrink-0">
          <span class="text-3xs font-bold text-muted-foreground uppercase flex items-center gap-1.5">
            <Tag class="w-3 h-3" />
            {t(msg`Tags:`)}
          </span>
          <For each={allTags()}>
            {(tag) => (
              <button
                type="button"
                onClick={() => {
                  handleTagChange(tagFilter() === tag ? undefined : tag);
                }}
                class="px-2.5 py-1 text-3xs font-bold border rounded-lg transition-all"
                classList={{
                  "bg-primary text-primary-foreground border-primary shadow-xs":
                    tagFilter() === tag,
                  "bg-background hover:bg-muted/40 border-border/40 text-foreground/80":
                    tagFilter() !== tag,
                }}
              >
                #{tag}
              </button>
            )}
          </For>
        </div>
      </Show>

      {/* Main Grid View */}
      <div class="flex-1 overflow-y-auto p-4 md:p-8">
        <Show
          when={groupedHighlights().length > 0}
          fallback={
            <div class="max-w-md mx-auto py-20 text-center border border-dashed border-border/30 bg-card/10 rounded-2xl p-6 motion-safe:animate-fade-in">
              <EmptyIllustration variant="highlights" class="mb-3 inline-flex" />
              <h3 class="text-sm font-bold text-foreground/85">{t(msg`No highlights found`)}</h3>
              <p class="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                {bookFilter() || categoryFilter() || tagFilter()
                  ? t(msg`You have no highlighted verses matching the active filters.`)
                  : t(msg`Start reading the Bible and highlight verses with your favorite colors.`)}
              </p>
            </div>
          }
        >
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-24">
            <For each={groupedHighlights()}>
              {(highlight) => {
                const [firstVerse] = highlight.verses;
                return (
                  <div class="group relative flex flex-col bg-card/30 border border-border/35 hover:border-primary/30 rounded-2xl overflow-hidden shadow-2xs hover:shadow-xs transition-all">
                    {/* Color Top Border Accent */}
                    <div
                      class="h-1.5 w-full shrink-0"
                      style={{ "background-color": highlight.color }}
                    />

                    <div class="p-5 flex flex-col justify-between flex-1 space-y-4">
                      <div class="space-y-2.5">
                        <div class="flex items-center justify-between">
                          <span class="text-3xs text-muted-foreground font-mono flex items-center gap-1.5">
                            <Calendar class="w-3 h-3" />
                            {new Date(highlight.createdAt).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                          <span
                            class="text-4xs uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded border"
                            style={{
                              "border-color": `${highlight.color}40`,
                              "background-color": `${highlight.color}10`,
                              color: highlight.color,
                            }}
                          >
                            {firstVerse?.version}
                          </span>
                        </div>

                        <h3 class="text-sm font-extrabold text-foreground/90 font-serif leading-snug">
                          {formatVerseReference(highlight.verses, t(msg`Highlight`))}
                        </h3>

                        <HighlightVersesText verses={highlight.verses} db={drizzle()} />

                        <Show when={highlight.tags.length > 0}>
                          <div class="flex flex-wrap gap-1.5">
                            <For each={highlight.tags}>
                              {(tag) => (
                                <span class="px-2 py-0.5 bg-muted/40 border border-border/30 text-3xs font-semibold rounded-md text-foreground/70">
                                  #{tag}
                                </span>
                              )}
                            </For>
                          </div>
                        </Show>
                      </div>

                      {/* Card Actions */}
                      <div class="flex items-center justify-between border-t border-border/20 pt-3">
                        <Show when={highlight.verses[0]}>
                          {(first) => (
                            <Link
                              to="/bible/$version/$bookAbbr/$chapter"
                              params={{
                                version: first().version,
                                bookAbbr: idToAbbr(first().book),
                                chapter: String(first().chapter),
                              }}
                              search={{
                                verse: first().verse,
                              }}
                              class="text-xs font-bold text-primary hover:text-primary/80 flex items-center gap-1.5 transition-colors"
                            >
                              <BookOpen class="w-3.5 h-3.5" />
                              <span>{t(msg`Read passage`)}</span>
                            </Link>
                          )}
                        </Show>

                        <div class="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              handleEdit(highlight);
                            }}
                            class="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors duration-150"
                            title={t(msg`Edit highlight`)}
                          >
                            <Edit3 class="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            disabled={!drizzle()}
                            onClick={() => {
                              void handleDelete(highlight);
                            }}
                            class="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t(msg`Delete highlight`)}
                          >
                            <Trash2 class="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
            </For>
          </div>
        </Show>
      </div>

      {/* Edit Drawer (color + tags) */}
      <Show when={editingHighlight()}>
        {(highlight) => (
          <div class="absolute inset-0 bg-background/40 backdrop-blur-xs z-40 flex justify-end">
            <div class="w-full sm:w-110 h-full bg-card border-l border-border/50 flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-200">
              <div class="p-5 border-b border-border/30 flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <Edit3 class="w-5 h-5 text-primary" />
                  <h3 class="text-sm font-bold">{t(msg`Edit Highlight`)}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingHighlightUrl(null)}
                  class="p-1.5 hover:bg-muted text-muted-foreground rounded-lg"
                >
                  <X class="w-4.5 h-4.5" />
                </button>
              </div>

              <div class="flex-1 overflow-y-auto p-5 space-y-6">
                <div class="space-y-1">
                  <span class="text-2xs font-bold text-muted-foreground uppercase tracking-wide">
                    {t(msg`Passage`)}
                  </span>
                  <p class="text-sm font-bold text-foreground/90 font-serif">
                    {formatVerseReference(highlight().verses, t(msg`Highlight`))}
                  </p>
                </div>

                {/* Color picker */}
                <div class="space-y-2">
                  <span class="text-2xs font-bold text-muted-foreground uppercase tracking-wide">
                    {t(msg`Color`)}
                  </span>
                  <div class="flex gap-2">
                    <For each={HIGHLIGHT_COLORS}>
                      {(color) => (
                        <button
                          type="button"
                          onClick={() => setEditorColor(color.hex)}
                          class="w-8 h-8 rounded-full border border-border/40 flex items-center justify-center transition-all scale-100 hover:scale-105 active:scale-95 shadow-2xs"
                          style={{ "background-color": color.hex }}
                          title={t(color.name)}
                        >
                          <Show when={editorColor() === color.hex}>
                            <Check class="w-4 h-4 text-white drop-shadow-md" />
                          </Show>
                        </button>
                      )}
                    </For>
                  </div>
                </div>

                {/* Tags editor */}
                <div class="space-y-2">
                  <label
                    for="highlight-tags-input"
                    class="text-2xs font-bold text-muted-foreground uppercase tracking-wide"
                  >
                    {t(msg`Tags`)}
                  </label>
                  <div class="relative">
                    <Tag class="w-3.5 h-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="highlight-tags-input"
                      type="text"
                      value={editorTags()}
                      onInput={(e) => setEditorTags(e.currentTarget.value)}
                      placeholder={t(msg`Comma-separated tags`)}
                      class="w-full bg-background border border-border/50 rounded-xl pl-9 pr-4 py-2.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div class="p-5 border-t border-border/30 bg-muted/10 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setEditingHighlightUrl(null)}
                  class="px-4 py-2 border border-border/50 hover:bg-muted text-xs font-semibold rounded-lg transition-colors"
                >
                  {t(msg`Cancel`)}
                </button>
                <button
                  type="button"
                  disabled={!drizzle() || !session.userId()}
                  onClick={() => {
                    void handleSaveEdit();
                  }}
                  class="px-5 py-2 bg-primary hover:bg-primary/95 text-primary-foreground text-xs font-semibold rounded-lg shadow-md active:scale-98 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t(msg`Save`)}
                </button>
              </div>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}
